import React, { useState } from 'react'
import { useTheme } from 'next-themes'
import ThemeToggle from './ThemeToggle.jsx'
import TopNavigation from './TopNavigation.jsx'
import useAsset from '@/hooks/useAsset'

function DesignSystemSimple() {
  const [activeSection, setActiveSection] = useState('overview')
  const { theme } = useTheme()

  // Cache-busted asset URL
  const logoTextSrc = useAsset('/tailtations-text.svg')
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <a 
                href="/"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                ← Back to Site
              </a>
              <div className="flex items-center gap-3">
                <img
                  src={logoTextSrc}
                  alt="Tailtations"
                  className="h-8 w-auto"
                />
                <h1 className="text-xl font-bold">Design System</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <span className="text-sm text-muted-foreground">v1.0.0</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="w-64 border-r border-border bg-muted/30 min-h-screen">
          <nav className="p-6">
            <div className="space-y-1">
              <button 
                onClick={() => setActiveSection('overview')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                  activeSection === 'overview' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Overview
              </button>
              <button 
                onClick={() => setActiveSection('colors')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                  activeSection === 'colors' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Colors
              </button>
              <button 
                onClick={() => setActiveSection('typography')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                  activeSection === 'typography' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Typography
              </button>
              <button 
                onClick={() => setActiveSection('components')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                  activeSection === 'components' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Components
              </button>
              <button 
                onClick={() => setActiveSection('auth')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                  activeSection === 'auth' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Authentication
              </button>
              <button 
                onClick={() => setActiveSection('navigation')}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                  activeSection === 'navigation' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Navigation
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {activeSection === 'overview' && (
              <>
                <div>
                  <h1 className="text-4xl font-bold mb-4 text-foreground">Design System Overview</h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    A comprehensive guide to the Tailtations design system, including colors, typography, 
                    components, and usage guidelines. All elements are theme-aware and automatically adapt 
                    between light and dark modes.
                  </p>
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-8">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="font-medium text-foreground" style={{ color: 'var(--foreground) !important' }}>Interactive Design System</span>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--foreground) !important', opacity: '0.8' }}>
                      Use the theme toggle in the header to switch between light and dark modes. 
                      All colors, components, and typography will update in real-time to show you 
                      how the design system adapts to different themes.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-2 text-card-foreground">Unified Design System</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      A single design system that works across all Tailtations applications with automatic theme switching.
                    </p>
                    <div className="space-y-2">
                      <span className="inline-block bg-primary text-primary-foreground px-2 py-1 rounded text-xs">Theme-Aware Colors</span>
                      <span className="inline-block bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs ml-2">Consistent Components</span>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-2 text-card-foreground">Applications</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Main website, investor portal, SECRET program, and design system all use the same unified theming.
                    </p>
                    <div className="space-y-2">
                      <span className="inline-block bg-accent text-accent-foreground px-2 py-1 rounded text-xs">Main Site</span>
                      <span className="inline-block bg-accent text-accent-foreground px-2 py-1 rounded text-xs ml-2">SECRET App</span>
                      <span className="inline-block bg-accent text-accent-foreground px-2 py-1 rounded text-xs ml-2">Design System</span>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold mb-4 text-card-foreground">Design Principles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold mb-2 text-card-foreground">Consistency</h4>
                      <p className="text-sm text-muted-foreground">
                        Use standardized components, colors, and spacing to create a cohesive experience.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 text-card-foreground">Accessibility</h4>
                      <p className="text-sm text-muted-foreground">
                        Ensure all components meet WCAG AA standards for color contrast and usability.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 text-card-foreground">Scalability</h4>
                      <p className="text-sm text-muted-foreground">
                        Design system grows with the product while maintaining brand consistency.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeSection === 'colors' && (
              <>
                <div>
                  <h1 className="text-4xl font-bold mb-4 text-foreground">Color Palette</h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    Our color system is designed to create visual hierarchy and maintain brand consistency across all platforms.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Theme-Aware Color Palette</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Colors automatically adapt based on the current theme. Switch between light and dark mode to see the changes.
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Primary Colors</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="w-full h-16 bg-primary rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Primary</p>
                            <p className="text-xs text-muted-foreground">bg-primary</p>
                            <p className="text-xs text-muted-foreground mt-1">CTAs, links, and brand elements</p>
                          </div>
                          <div className="text-center">
                            <div className="w-full h-16 bg-secondary rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Secondary</p>
                            <p className="text-xs text-muted-foreground">bg-secondary</p>
                            <p className="text-xs text-muted-foreground mt-1">Subtle backgrounds and elements</p>
                          </div>
                          <div className="text-center">
                            <div className="w-full h-16 bg-accent rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Accent</p>
                            <p className="text-xs text-muted-foreground">bg-accent</p>
                            <p className="text-xs text-muted-foreground mt-1">Highlighted elements</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Background Colors</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="w-full h-16 bg-background rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Background</p>
                            <p className="text-xs text-muted-foreground">bg-background</p>
                            <p className="text-xs text-muted-foreground mt-1">Main page background</p>
                          </div>
                          <div className="text-center">
                            <div className="w-full h-16 bg-card rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Card</p>
                            <p className="text-xs text-muted-foreground">bg-card</p>
                            <p className="text-xs text-muted-foreground mt-1">Card and panel backgrounds</p>
                          </div>
                          <div className="text-center">
                            <div className="w-full h-16 bg-muted rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Muted</p>
                            <p className="text-xs text-muted-foreground">bg-muted</p>
                            <p className="text-xs text-muted-foreground mt-1">Subtle backgrounds</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Text Colors</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="w-full h-16 bg-foreground rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Foreground</p>
                            <p className="text-xs text-muted-foreground">text-foreground</p>
                            <p className="text-xs text-muted-foreground mt-1">Primary text color</p>
                          </div>
                          <div className="text-center">
                            <div className="w-full h-16 bg-muted-foreground rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Muted Foreground</p>
                            <p className="text-xs text-muted-foreground">text-muted-foreground</p>
                            <p className="text-xs text-muted-foreground mt-1">Secondary text color</p>
                          </div>
                          <div className="text-center">
                            <div className="w-full h-16 bg-primary-foreground rounded-lg mb-2 border border-border"></div>
                            <p className="text-sm font-medium text-card-foreground">Primary Foreground</p>
                            <p className="text-xs text-muted-foreground">text-primary-foreground</p>
                            <p className="text-xs text-muted-foreground mt-1">Text on primary backgrounds</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Border & Utility Colors</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="w-full h-16 bg-border rounded-lg mb-2 border border-border"></div>
                        <p className="text-sm font-medium text-card-foreground">Border</p>
                        <p className="text-xs text-muted-foreground">border-border</p>
                        <p className="text-xs text-muted-foreground mt-1">Element borders and dividers</p>
                      </div>
                      <div className="text-center">
                        <div className="w-full h-16 bg-input rounded-lg mb-2 border border-border"></div>
                        <p className="text-sm font-medium text-card-foreground">Input</p>
                        <p className="text-xs text-muted-foreground">bg-input</p>
                        <p className="text-xs text-muted-foreground mt-1">Form input backgrounds</p>
                      </div>
                      <div className="text-center">
                        <div className="w-full h-16 bg-ring rounded-lg mb-2 border border-border"></div>
                        <p className="text-sm font-medium text-card-foreground">Ring</p>
                        <p className="text-xs text-muted-foreground">ring-ring</p>
                        <p className="text-xs text-muted-foreground mt-1">Focus rings and highlights</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Current Theme: {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      All colors above automatically adapt to the current theme. The theme toggle in the header allows you to switch between light and dark modes to see how the color palette changes.
                    </p>
                    <div className="bg-muted border border-border rounded-lg p-4">
                      <h4 className="font-medium mb-2 text-card-foreground">Theme Variables</h4>
                      <p className="text-sm text-muted-foreground">
                        Colors are defined using CSS custom properties that change based on the theme class applied to the document root.
                        This ensures consistent theming across all components without hardcoded color values.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeSection === 'typography' && (
              <>
                <div>
                  <h1 className="text-4xl font-bold mb-4 text-foreground">Typography</h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    Our typography system ensures readability and maintains visual hierarchy across all content.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Font Families</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-2 text-card-foreground">Heading Font</h4>
                        <h2 className="text-3xl font-bold text-card-foreground mb-2">League Spartan</h2>
                        <p className="text-sm text-muted-foreground">Used for all headings (h1, h2, h3, h4, h5, h6)</p>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2 text-card-foreground">Body Font</h4>
                        <p className="text-2xl font-sans text-card-foreground">Open Sans</p>
                        <p className="text-sm text-muted-foreground mt-1">Used for body text, paragraphs, and UI elements</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Heading Scale</h3>
                    <div className="space-y-4">
                      <div>
                        <h1 className="text-4xl font-bold text-card-foreground mb-2">Heading 1</h1>
                        <p className="text-sm text-muted-foreground">4xl / 2.25rem / 36px - Page titles</p>
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-card-foreground mb-2">Heading 2</h2>
                        <p className="text-sm text-muted-foreground">3xl / 1.875rem / 30px - Section titles</p>
                      </div>
                      <div>
                        <h3 className="text-2xl font-semibold text-card-foreground mb-2">Heading 3</h3>
                        <p className="text-sm text-muted-foreground">2xl / 1.5rem / 24px - Subsection titles</p>
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-card-foreground mb-2">Heading 4</h4>
                        <p className="text-sm text-muted-foreground">xl / 1.25rem / 20px - Card titles</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Body Text</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-lg text-card-foreground mb-2">Large Body Text</p>
                        <p className="text-sm text-muted-foreground">lg / 1.125rem / 18px - Lead paragraphs</p>
                      </div>
                      <div>
                        <p className="text-base text-card-foreground mb-2">Regular Body Text</p>
                        <p className="text-sm text-muted-foreground">base / 1rem / 16px - Standard body text</p>
                      </div>
                      <div>
                        <p className="text-sm text-card-foreground mb-2">Small Body Text</p>
                        <p className="text-sm text-muted-foreground">sm / 0.875rem / 14px - Captions and metadata</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeSection === 'components' && (
              <>
                <div>
                  <h1 className="text-4xl font-bold mb-4 text-foreground">Components</h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    Reusable UI components that maintain consistency and follow our design principles.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Theme-Aware Buttons</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      All buttons automatically adapt to the current theme. Switch between light and dark mode to see the changes.
                    </p>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Primary Buttons</h4>
                        <div className="flex gap-4 flex-wrap">
                          <button className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors">
                            Primary Button
                          </button>
                          <button className="bg-primary text-primary-foreground px-6 py-3 rounded-md hover:bg-primary/90 transition-colors">
                            Large Primary
                          </button>
                          <button className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50">
                            Disabled Primary
                          </button>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Secondary Buttons</h4>
                        <div className="flex gap-4 flex-wrap">
                          <button className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/80 transition-colors">
                            Secondary Button
                          </button>
                          <button className="border border-border text-foreground px-4 py-2 rounded-md hover:bg-muted transition-colors">
                            Outline Button
                          </button>
                          <button className="bg-muted text-muted-foreground px-4 py-2 rounded-md hover:bg-muted/80 transition-colors">
                            Ghost Button
                          </button>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Destructive Buttons</h4>
                        <div className="flex gap-4 flex-wrap">
                          <button className="bg-destructive text-destructive-foreground px-4 py-2 rounded-md hover:bg-destructive/90 transition-colors">
                            Delete Button
                          </button>
                          <button className="border border-destructive text-destructive px-4 py-2 rounded-md hover:bg-destructive/10 transition-colors">
                            Destructive Outline
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Theme-Aware Cards</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Cards automatically adapt their backgrounds, borders, and text colors based on the current theme.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold mb-2 text-card-foreground">Basic Card</h4>
                        <p className="text-sm text-muted-foreground">Simple card with border and shadow</p>
                        <div className="mt-3">
                          <span className="inline-block bg-primary text-primary-foreground px-2 py-1 rounded text-xs">Primary Badge</span>
                        </div>
                      </div>
                      <div className="bg-muted border border-border rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-card-foreground">Subtle Card</h4>
                        <p className="text-sm text-muted-foreground">Card with subtle background</p>
                        <div className="mt-3">
                          <span className="inline-block bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs">Secondary Badge</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Theme-Aware Form Elements</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Form elements use theme-aware colors for backgrounds, borders, and focus states.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">Input Field</label>
                        <input 
                          type="text" 
                          placeholder="Enter text..." 
                          className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">Textarea</label>
                        <textarea 
                          placeholder="Enter message..." 
                          rows={3}
                          className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">Select Dropdown</label>
                        <select className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent">
                          <option>Choose an option...</option>
                          <option>Option 1</option>
                          <option>Option 2</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Current Theme: {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      All components above automatically adapt to the current theme. Use the theme toggle in the header to switch between light and dark modes and see how all elements change their appearance.
                    </p>
                    <div className="bg-muted border border-border rounded-lg p-4">
                      <h4 className="font-medium mb-2 text-card-foreground">Component Theming</h4>
                      <p className="text-sm text-muted-foreground">
                        Components use CSS custom properties and Tailwind's theme-aware classes to ensure consistent theming across the entire application.
                        This approach maintains design consistency while providing seamless theme switching.
                      </p>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Icon Styling</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Icons automatically adapt their colors based on their background context for optimal visibility.
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Icons on Primary Backgrounds</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Icons on red/primary backgrounds are automatically white in both light and dark modes for optimal contrast.
                        </p>
                        <div className="flex gap-4 flex-wrap">
                          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                            <div className="w-6 h-6 text-white">📅</div>
                          </div>
                          <div className="w-12 h-12 bg-secret-gradient rounded-lg flex items-center justify-center">
                            <div className="w-6 h-6 text-white">⭐</div>
                          </div>
                          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                            <div className="w-6 h-6 text-white">📦</div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Icons on Light Backgrounds</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Icons on light backgrounds use theme-appropriate colors (dark in light mode, light in dark mode).
                        </p>
                        <div className="flex gap-4 flex-wrap">
                          <div className="w-12 h-12 bg-card border border-border rounded-lg flex items-center justify-center">
                            <div className="w-6 h-6 text-foreground">📅</div>
                          </div>
                          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                            <div className="w-6 h-6 text-foreground">⭐</div>
                          </div>
                          <div className="w-12 h-12 bg-background border border-border rounded-lg flex items-center justify-center">
                            <div className="w-6 h-6 text-foreground">📦</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeSection === 'auth' && (
              <>
                <div>
                  <h1 className="text-4xl font-bold mb-4 text-foreground">Authentication Components</h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    Standardized components for authentication pages that ensure consistency across login, register, and other auth flows.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">AuthPageLayout</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Complete layout component that combines header, container, and form styling for authentication pages.
                    </p>
                    <div className="bg-muted border border-border rounded-lg p-4 mb-4">
                      <h4 className="font-medium mb-2 text-card-foreground">Usage</h4>
                      <pre className="text-xs text-muted-foreground overflow-x-auto">
{`import { AuthPageLayout, AuthFormHeader, AuthFormField, AuthFormButton } from './AuthLayout'

<AuthPageLayout 
  title="Sign In" 
  subtitle="Welcome back to SECRET"
  backTo="/"
  backText="Back"
>
  <AuthFormHeader title="Sign In" subtitle="Welcome back to SECRET" />
  <AuthFormField 
    label="Email" 
    type="email" 
    placeholder="Enter your email"
    required
  />
  <AuthFormButton>Sign In</AuthFormButton>
</AuthPageLayout>`}
                      </pre>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Individual Components</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Use individual components for more control over layout and styling.
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">AuthFormField</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Standardized form field with label, input, and optional icon.
                        </p>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Email Address</label>
                            <input 
                              type="email" 
                              placeholder="Enter your email"
                              className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Password</label>
                            <input 
                              type="password" 
                              placeholder="Enter your password"
                              className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">AuthFormButton</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Standardized button with loading states and variants.
                        </p>
                        <div className="flex gap-4 flex-wrap">
                          <button className="bg-primary text-primary-foreground px-6 py-3 text-sm font-medium rounded-md hover:bg-primary/90 transition-colors">
                            Sign In
                          </button>
                          <button className="bg-secondary text-secondary-foreground px-6 py-3 text-sm font-medium rounded-md hover:bg-secondary/90 transition-colors">
                            Create Account
                          </button>
                          <button className="border border-border bg-background hover:bg-muted px-6 py-3 text-sm font-medium rounded-md transition-colors">
                            Cancel
                          </button>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">AuthFormTextLink</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Text with embedded link, properly styled for both themes.
                        </p>
                        <div className="flex justify-center">
                          <span className="text-sm text-muted-foreground">
                            Don't have an account?{' '}
                            <a href="#" className="text-primary hover:text-primary/80 font-medium transition-colors">
                              Join SECRET
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Theme Integration</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      All authentication components automatically adapt to light and dark themes, ensuring consistent styling across your application.
                    </p>
                    <div className="bg-muted border border-border rounded-lg p-4">
                      <h4 className="font-medium mb-2 text-card-foreground">Key Features</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Theme-aware logo switching (light/dark)</li>
                        <li>• Consistent color scheme across all components</li>
                        <li>• Proper contrast ratios for accessibility</li>
                        <li>• Responsive design for all screen sizes</li>
                        <li>• Loading and error states</li>
                        <li>• Form validation support</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeSection === 'navigation' && (
              <>
                <div>
                  <h1 className="text-4xl font-bold mb-4 text-foreground">Navigation Components</h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    Reusable navigation components that provide consistent header layouts across different pages.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">TopNavigation</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      A standardized header component with back button, centered logo, and theme toggle. Used across authentication and dashboard pages.
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Default Usage</h4>
                        <div className="border border-border rounded-lg overflow-hidden">
                          <div className="bg-background">
                            <TopNavigation />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Custom Back Button</h4>
                        <div className="border border-border rounded-lg overflow-hidden">
                          <div className="bg-background">
                            <TopNavigation 
                              backTo="/custom-path" 
                              backText="Custom Back" 
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Without Back Button</h4>
                        <div className="border border-border rounded-lg overflow-hidden">
                          <div className="bg-background">
                            <TopNavigation 
                              showBackButton={false}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3 text-card-foreground">Custom Logo</h4>
                        <div className="border border-border rounded-lg overflow-hidden">
                          <div className="bg-background">
                            <TopNavigation 
                              logo="/tailtations.svg"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Props</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3 font-medium text-card-foreground">Prop</th>
                            <th className="text-left py-2 px-3 font-medium text-card-foreground">Type</th>
                            <th className="text-left py-2 px-3 font-medium text-card-foreground">Default</th>
                            <th className="text-left py-2 px-3 font-medium text-card-foreground">Description</th>
                          </tr>
                        </thead>
                        <tbody className="text-muted-foreground">
                          <tr className="border-b border-border">
                            <td className="py-2 px-3 font-mono text-xs">backTo</td>
                            <td className="py-2 px-3">string</td>
                            <td className="py-2 px-3">"/"</td>
                            <td className="py-2 px-3">URL path for the back button link</td>
                          </tr>
                          <tr className="border-b border-border">
                            <td className="py-2 px-3 font-mono text-xs">backText</td>
                            <td className="py-2 px-3">string</td>
                            <td className="py-2 px-3">"Back"</td>
                            <td className="py-2 px-3">Text displayed next to the back arrow</td>
                          </tr>
                          <tr className="border-b border-border">
                            <td className="py-2 px-3 font-mono text-xs">logo</td>
                            <td className="py-2 px-3">string</td>
                            <td className="py-2 px-3">"/tailtations-text.svg"</td>
                            <td className="py-2 px-3">Path to the logo image</td>
                          </tr>
                          <tr>
                            <td className="py-2 px-3 font-mono text-xs">showBackButton</td>
                            <td className="py-2 px-3">boolean</td>
                            <td className="py-2 px-3">true</td>
                            <td className="py-2 px-3">Whether to show the back button</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold mb-4 text-card-foreground">Usage Examples</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-2 text-card-foreground">Basic Usage</h4>
                        <div className="bg-muted border border-border rounded-lg p-4">
                          <pre className="text-sm text-muted-foreground overflow-x-auto">
{`import TopNavigation from './components/TopNavigation'

function MyPage() {
  return (
    <div className="min-h-screen bg-background">
      <TopNavigation />
      {/* Your page content */}
    </div>
  )
}`}
                          </pre>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-2 text-card-foreground">Custom Configuration</h4>
                        <div className="bg-muted border border-border rounded-lg p-4">
                          <pre className="text-sm text-muted-foreground overflow-x-auto">
{`<TopNavigation 
  backTo="/dashboard" 
  backText="Dashboard" 
  logo="/custom-logo.svg"
  showBackButton={true}
/>`}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default DesignSystemSimple
